import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import Modal from "react-bootstrap/Modal";
import { postFeedback } from "@redux/Hermes/hermes.action";
import useHermesAnalytics from "@containers/Hermes/hooks/useHermesAnalytics";
import type { AppDispatch } from "@containers/Hermes/types";

type MessageFeedbackProps = {
  messageId: number;
  conversationId: string;
};

type FeedbackChoice = "up" | "down" | null;

const ICON_BASE = "tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-md tw-border-none tw-transition-all tw-duration-150 tw-cursor-pointer tw-bg-transparent";

export default function MessageFeedback({ messageId, conversationId }: MessageFeedbackProps) {
  const dispatch = useDispatch<AppDispatch>();
  const analytics = useHermesAnalytics();

  const [choice, setChoice] = useState<FeedbackChoice>(null);
  const [submitted, setSubmitted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submitFeedback = useCallback(
    async (helpful: boolean, optionalComment?: string) => {
      setSubmitting(true);
      try {
        await dispatch(
          postFeedback({
            conversationId,
            messageId,
            helpful,
            comment: optionalComment || undefined,
          })
        );
        setSubmitted(true);
      } finally {
        setSubmitting(false);
      }
    },
    [dispatch, conversationId, messageId]
  );

  const handleThumbsUp = useCallback(() => {
    if (submitted || submitting) return;
    setChoice("up");
    analytics.feedbackPositive({ conversationId, messageId });
    submitFeedback(true);
  }, [submitted, submitting, submitFeedback, analytics, conversationId, messageId]);

  const handleThumbsDown = useCallback(() => {
    if (submitted || submitting) return;
    setChoice("down");
    setShowModal(true);
  }, [submitted, submitting]);

  const handleModalSubmit = useCallback(() => {
    setShowModal(false);
    const trimmed = comment.trim();
    analytics.feedbackNegative({ conversationId, messageId, hasComment: trimmed.length > 0, commentLength: trimmed.length });
    submitFeedback(false, comment);
  }, [comment, submitFeedback, analytics, conversationId, messageId]);

  const handleModalSkip = useCallback(() => {
    setShowModal(false);
    analytics.feedbackNegative({ conversationId, messageId, hasComment: false, commentLength: 0 });
    submitFeedback(false);
  }, [submitFeedback, analytics, conversationId, messageId]);

  if (submitted) {
    return <span className="tw-text-[11px] tw-text-gray-400 tw-inline-block">Thanks for your feedback</span>;
  }

  return (
    <>
      <div className="tw-flex tw-items-center tw-gap-1" aria-label="Was this response helpful?">
        <button onClick={handleThumbsUp} disabled={submitting} aria-label="Helpful" title="Helpful" className={`${ICON_BASE} ${choice === "up" ? "tw-bg-success-50 tw-text-success-600" : "tw-text-gray-400 hover:tw-text-success-600 hover:tw-bg-gray-50"} disabled:tw-opacity-50 disabled:tw-cursor-default`}>
          <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 -960 960 960" width="20" fill="currentColor" className="tw-text-gray-900">
            <path d="M720-120H280v-520l280-280 50 50q7 7 11.5 19t4.5 23v14l-44 174h258q32 0 56 24t24 56v80q0 7-2 15t-4 15L794-168q-9 20-30 34t-44 14Zm-360-80h360l120-280v-80H480l54-220-174 174v406Zm0-406v406-406Zm-80-34v80H160v360h120v80H80v-520h200Z" />
          </svg>
        </button>

        <button onClick={handleThumbsDown} disabled={submitting} aria-label="Not helpful" title="Not helpful" className={`${ICON_BASE} ${choice === "down" ? "tw-bg-error-50 tw-text-error-500" : "tw-text-gray-400 hover:tw-text-error-500 hover:tw-bg-gray-50"} disabled:tw-opacity-50 disabled:tw-cursor-default`}>
          <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 -960 960 960" width="20" fill="currentColor" className="tw-text-gray-900">
            <path d="M240-840h440v520L400-40l-50-50q-7-7-11.5-19t-4.5-23v-14l44-174H120q-32 0-56-24t-24-56v-80q0-7 2-15t4-15l120-282q9-20 30-34t44-14Zm360 80H240L120-480v80h360l-54 220 174-174v-406Zm0 406v-406 406Zm80 34v-80h120v-360H680v-80h200v520H680Z" />
          </svg>
        </button>
      </div>

      <Modal show={showModal} onHide={handleModalSkip} centered size="sm" backdrop="static">
        <Modal.Header closeButton className="tw-border-b tw-border-gray-100 tw-pb-3">
          <Modal.Title className="tw-text-sm tw-font-semibold tw-text-gray-800">What went wrong?</Modal.Title>
        </Modal.Header>

        <Modal.Body className="tw-py-4">
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Tell us what could be improved (optional)" rows={4} className="tw-w-full tw-resize-none tw-rounded-lg tw-border tw-border-gray-200 tw-text-sm tw-text-gray-800 tw-placeholder-gray-400 tw-p-3 focus:tw-outline-none focus:tw-border-primary-purple-400 tw-transition-colors" aria-label="Feedback comment" autoFocus />
        </Modal.Body>

        <Modal.Footer className="tw-border-t tw-border-gray-100 tw-pt-3 tw-gap-2">
          <button onClick={handleModalSkip} className="tw-text-sm tw-text-gray-500 tw-px-4 tw-py-2 tw-rounded-lg hover:tw-bg-gray-100 tw-transition-colors tw-border-none tw-bg-transparent tw-cursor-pointer">
            Skip
          </button>
          <button onClick={handleModalSubmit} disabled={submitting} className="tw-text-sm tw-font-medium tw-text-white tw-bg-primary-purple-600 hover:tw-bg-primary-purple-700 tw-px-4 tw-py-2 tw-rounded-lg tw-transition-colors tw-border-none tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-default">
            {submitting ? "Submitting…" : "Submit"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
